import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ProtectedImage from "../../components/ProtectedImage";
import SocialCard from "../../components/SocialCard";
import RelPill from "../RelPill";
import { useT } from "../../context/LanguageContext";
import {
  Heart, MessageCircle, ChevronRight, Search, Send, Inbox, Trophy,
  User as UserIcon, Globe2, Gamepad2, Users2,
} from "lucide-react";

/**
 * V2 / Phase 3 — "Social" tab. Two-sub-tab layout: Feed | Messages.
 * Reuses existing endpoints (/global-feed, /social/browse, /dm/threads, follow).
 */
export default function SocialV2() {
  const { t } = useT();
  const [sub, setSub] = useState("feed");
  return (
    <div className="px-5 pt-5 pb-2 space-y-4" data-testid="v2-social">
      <header data-testid="v2-social-header">
        <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#A5B4FC]">{t("v2.soc.kicker")}</div>
        <h1 className="text-2xl font-bold text-white mt-0.5">{t("v2.soc.title")}</h1>
      </header>

      <div className="flex gap-1 bg-[#0F0F18] border border-[#1F1F2A] rounded-2xl p-1" data-testid="v2-social-subtabs">
        <SubTab active={sub === "feed"} onClick={() => setSub("feed")} testId="v2-social-tab-feed">{t("v2.soc.tab.feed")}</SubTab>
        <SubTab active={sub === "messages"} onClick={() => setSub("messages")} testId="v2-social-tab-messages">{t("v2.soc.tab.messages")}</SubTab>
        <SubTab active={sub === "discover"} onClick={() => setSub("discover")} testId="v2-social-tab-discover">{t("v2.soc.tab.discover")}</SubTab>
      </div>

      {sub === "feed" && <FeedView />}
      {sub === "messages" && <MessagesView />}
      {sub === "discover" && <DiscoverView />}
    </div>
  );
}

function SubTab({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all ${
        active
          ? "bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white shadow-lg shadow-[#7C3AED]/20"
          : "text-[#71717A] hover:text-[#A1A1AA]"
      }`}
    >
      {children}
    </button>
  );
}

// ============================== FEED ==============================
function FeedView() {
  const { t } = useT();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/global-feed");
        if (!cancelled) setPosts(Array.isArray(data) ? data : []);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <Loading />;
  if (posts.length === 0) return <EmptyState icon={Trophy} text={t("v2.soc.feed.empty")} />;

  return (
    <div className="space-y-3" data-testid="v2-feed-list">
      {posts.slice(0, 30).map((p) => <FeedPost key={p.id} p={p} onUpdate={(np) => setPosts((prev) => prev.map((x) => x.id === np.id ? np : x))} />)}
    </div>
  );
}

function FeedPost({ p, onUpdate }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const liked = !!p.liked_by_user;
  const likes = p.likes_count ?? 0;
  const commentsCount = p.comments_count ?? 0;
  const authorUsername = p.author_username || p.username || "ePlay";

  const toggleLike = async () => {
    if (busy) return;
    // Optimistic update — flip immediately so the tap feels instant.
    const optimistic = {
      ...p,
      liked_by_user: !liked,
      likes_count: Math.max(0, likes + (liked ? -1 : 1)),
    };
    onUpdate(optimistic);
    setBusy(true);
    try {
      const { data } = await api.post(`/feed/${p.id}/like`);
      onUpdate({ ...optimistic, liked_by_user: !!data.liked, likes_count: data.count ?? optimistic.likes_count });
    } catch {
      onUpdate(p); // rollback
    } finally {
      setBusy(false);
    }
  };

  const onCommentsChange = (delta) => {
    onUpdate({ ...p, comments_count: Math.max(0, commentsCount + delta) });
  };

  return (
    <article className="bg-[#12121C] border border-[#1F1F2A] rounded-2xl overflow-hidden" data-testid={`v2-feed-post-${p.id}`}>
      <div className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#3B82F6] flex items-center justify-center font-bold text-white text-sm">
            {authorUsername.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 min-w-0">
              <Link to={`/v2/players/${authorUsername}`} className="text-sm font-semibold text-white hover:text-[#A5B4FC] truncate">
                {authorUsername}
              </Link>
              {p.author_reliability != null && (
                <RelPill score={p.author_reliability} flagged={p.author_reliability_flagged} testId={`v2-feed-rel-${p.id}`} />
              )}
            </div>
            <div className="text-[11px] text-[#71717A]">{relativeTime(p.created_at)}</div>
          </div>
        </div>
        {p.text && <p className="text-sm text-white mt-3 whitespace-pre-wrap break-words leading-relaxed">{p.text}</p>}
      </div>
      {p.image_path && (
        <div className="px-4 pb-3">
          <ProtectedImage path={p.image_path} alt="post" className="w-full max-h-96 object-cover rounded-xl border border-[#1F1F2A]" />
        </div>
      )}
      <div className="border-t border-[#1F1F2A] px-2 py-1.5 flex items-center gap-1">
        <button
          onClick={toggleLike}
          disabled={busy}
          data-testid={`v2-feed-like-${p.id}`}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${liked ? "text-[#EF4444]" : "text-[#71717A] hover:text-[#A1A1AA]"}`}
        >
          <Heart className={`w-4 h-4 ${liked ? "fill-current" : ""}`} /> {likes}
        </button>
        <button
          onClick={() => setCommentsOpen((v) => !v)}
          data-testid={`v2-feed-comments-${p.id}`}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${commentsOpen ? "text-[#A5B4FC]" : "text-[#71717A] hover:text-[#A1A1AA]"}`}
        >
          <MessageCircle className="w-4 h-4" /> {commentsCount}
        </button>
      </div>
      {commentsOpen && (
        <CommentsPanel postId={p.id} viewer={user} onCountChange={onCommentsChange} />
      )}
    </article>
  );
}

function CommentsPanel({ postId, viewer, onCountChange }) {
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/feed/${postId}/comments`);
      setComments(Array.isArray(data) ? data : []);
    } catch { setComments([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [postId]);

  const send = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/feed/${postId}/comments`, { text: body });
      setComments((prev) => [...(prev || []), data]);
      setText("");
      onCountChange?.(+1);
    } catch { /* noop */ }
    finally { setBusy(false); }
  };

  const remove = async (cid) => {
    try {
      await api.delete(`/feed/comments/${cid}`);
      setComments((prev) => (prev || []).filter((c) => c.id !== cid));
      onCountChange?.(-1);
    } catch { /* noop */ }
  };

  return (
    <div className="border-t border-[#1F1F2A] bg-[#0F0F18] px-4 py-3 space-y-2.5" data-testid={`v2-feed-comments-panel-${postId}`}>
      {comments === null ? (
        <div className="text-[11px] text-[#71717A] text-center py-2">Loading…</div>
      ) : comments.length === 0 ? (
        <div className="text-[11px] text-[#71717A] text-center py-2">Be the first to comment.</div>
      ) : (
        <ul className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
          {comments.map((c) => {
            const canDelete = viewer && (c.user_id === viewer.id || viewer.role === "admin");
            return (
              <li key={c.id} className="flex items-start gap-2" data-testid={`v2-feed-comment-${c.id}`}>
                <div className="w-6 h-6 shrink-0 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#3B82F6] flex items-center justify-center text-[10px] font-bold text-white">
                  {(c.username || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-semibold text-white truncate">
                    {c.username}
                    <span className="ml-1.5 font-normal text-[#71717A]">{relativeTime(c.created_at)}</span>
                  </div>
                  <div className="text-[12px] text-[#D4D4D8] break-words whitespace-pre-wrap leading-snug">{c.text}</div>
                </div>
                {canDelete && (
                  <button
                    onClick={() => remove(c.id)}
                    data-testid={`v2-feed-comment-delete-${c.id}`}
                    className="text-[10px] text-[#71717A] hover:text-[#EF4444] shrink-0"
                  >×</button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {viewer && (
        <div className="flex items-center gap-1.5 pt-1">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Write a comment…"
            data-testid={`v2-feed-comment-input-${postId}`}
            maxLength={1000}
            style={{ fontSize: "16px" }}
            className="flex-1 px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white placeholder-[#52525B]"
          />
          <button
            onClick={send}
            disabled={busy || !text.trim()}
            data-testid={`v2-feed-comment-send-${postId}`}
            className="px-3 py-2 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white text-xs font-semibold disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}

// ============================== MESSAGES ==============================
// Unified inbox — surfaces only chats where the user has actually received a
// message (across DMs, league chat, friendlies, pro clubs, WC match + group).
// Backed by /api/inbox/threads which does the cross-collection roll-up.
function MessagesView() {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/inbox/threads");
        if (!cancelled) setItems(Array.isArray(data) ? data : []);
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <Loading />;
  if (items.length === 0) {
    return <EmptyState icon={Inbox} text="No active chats yet. When someone messages you in a DM, league, friendly, Pro Clubs, or World Cup match, it'll show up here." />;
  }

  return (
    <div className="space-y-1.5" data-testid="v2-messages-list">
      {items.map((it) => <InboxRow key={it.key} item={it} />)}
    </div>
  );
}

// Icon + accent per inbox source type.
const INBOX_VISUAL = {
  dm:        { Icon: UserIcon,        bg: "from-[#7C3AED] to-[#3B82F6]", testKey: "dm" },
  league:    { Icon: Trophy,          bg: "from-[#7C3AED] to-[#3B82F6]", testKey: "league" },
  friendlies:{ Icon: Globe2,          bg: "from-[#0c1f4d] to-[#1a3a8c]", testKey: "friendlies" },
  proclubs:  { Icon: Gamepad2,        bg: "from-[#10B981] to-[#3B82F6]", testKey: "proclubs" },
  wct_match: { Icon: Trophy,          bg: "from-[#FFD700] to-[#FFB000]", testKey: "wct-match" },
  wct_group: { Icon: Users2,          bg: "from-[#FFD700] to-[#FFB000]", testKey: "wct-group" },
};

function InboxRow({ item }) {
  const visual = INBOX_VISUAL[item.type] || INBOX_VISUAL.dm;
  const { Icon, bg } = visual;
  const last = item.last_message || {};
  const unread = item.unread || 0;
  return (
    <Link
      to={item.link}
      data-testid={`v2-inbox-row-${visual.testKey}-${item.key.split(":").slice(1).join("-")}`}
      className="flex items-center gap-3 bg-[#12121C] hover:bg-[#161620] border border-[#1F1F2A] rounded-2xl p-3 transition-colors"
    >
      <div className={`relative w-12 h-12 rounded-full bg-gradient-to-br ${bg} flex items-center justify-center font-bold text-white flex-shrink-0`}>
        <Icon className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#070710]">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold text-white truncate">
            {item.title || "Chat"}
          </span>
          {last.created_at && <span className="text-[10px] text-[#52525B] flex-shrink-0">{relativeTime(last.created_at)}</span>}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-[#A5B4FC] truncate mt-0.5">{item.subtitle}</div>
        <div className={`text-xs truncate mt-0.5 ${unread > 0 ? "text-white font-medium" : "text-[#71717A]"}`}>
          {last.from_username && item.type !== "dm" ? <span className="text-[#A1A1AA]">@{last.from_username}: </span> : null}
          {last.text || "[message]"}
        </div>
      </div>
    </Link>
  );
}

// ============================== DISCOVER ==============================
function DiscoverView() {
  const { user } = useAuth();
  const { t } = useT();
  const [results, setResults] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(20);
  const sentinelRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/social/browse?limit=500");
        if (!cancelled) {
          const rows = data?.rows || data?.players || (Array.isArray(data) ? data : []);
          setResults(Array.isArray(rows) ? rows : []);
        }
      } catch { /* noop */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const exMe = results.filter((p) => p.id !== user?.id);
    if (!search.trim()) return exMe;
    const q = search.trim().toLowerCase();
    return exMe.filter((p) => (p.username || "").toLowerCase().includes(q) || (p.club_name || "").toLowerCase().includes(q));
  }, [search, results, user?.id]);

  // Reset visible window whenever the search filter changes
  useEffect(() => { setVisibleCount(20); }, [search]);

  // Infinite scroll: bump visibleCount when the sentinel enters the viewport
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisibleCount((c) => Math.min(filtered.length, c + 20));
      }
    }, { rootMargin: "300px" });
    io.observe(node);
    return () => io.disconnect();
  }, [filtered.length]);

  const toggle = async (p) => {
    try {
      if (p.is_following) await api.delete(`/users/${p.id}/follow`);
      else await api.post(`/users/${p.id}/follow`);
      setResults((prev) => prev.map((x) => x.id === p.id ? { ...x, is_following: !p.is_following } : x));
    } catch { /* noop */ }
  };

  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;

  return (
    <div className="space-y-3" data-testid="v2-discover">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("v2.soc.search")}
          data-testid="v2-discover-search"
          className="w-full bg-[#0F0F18] border border-[#1F1F2A] rounded-2xl pl-10 pr-3 py-2.5 text-sm text-white placeholder-[#52525B] focus:border-[#7C3AED] outline-none"
        />
      </div>
      {loading ? <Loading /> : filtered.length === 0 ? (
        <EmptyState icon={Search} text={search ? t("v2.soc.search.empty") : t("v2.soc.discover.empty")} />
      ) : (
        <>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#71717A]" data-testid="v2-discover-counter">
            Showing {visible.length} of {filtered.length} players
          </div>
          <div className="space-y-3" data-testid="v2-discover-list">
            {visible.map((p) => (
              <SocialCard
                key={p.id}
                p={p}
                onFollow={toggle}
                viewer={user}
                linkPrefix="/v2/players/"
                onRemoved={(uid) => setResults((prev) => prev.filter((r) => r.id !== uid))}
              />
            ))}
          </div>
          {hasMore && (
            <div ref={sentinelRef} className="py-6 text-center text-[10px] font-mono uppercase tracking-widest text-[#52525B]" data-testid="v2-discover-sentinel">
              Loading more…
            </div>
          )}
          {!hasMore && filtered.length > 20 && (
            <div className="py-6 text-center text-[10px] font-mono uppercase tracking-widest text-[#52525B]" data-testid="v2-discover-end">
              — End of list —
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================== HELPERS ==============================
function Loading() {
  return <div className="py-10 text-center text-sm text-[#52525B]" data-testid="v2-loading">Loading…</div>;
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="bg-[#12121C] border border-dashed border-[#1F1F2A] rounded-2xl p-8 flex flex-col items-center text-center" data-testid="v2-empty">
      <Icon className="w-7 h-7 text-[#52525B] mb-3" />
      <div className="text-sm text-[#71717A] max-w-[260px]">{text}</div>
    </div>
  );
}

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
